import React from 'react'

import ScreenshotPdf from 'features/planner/pages/MasterCampaignPlanPage/ScreenshotPdf'

import { generatePdfBlob } from './pdfDownloader'

export interface ScreenshotToPdfResult {
  success: boolean
  pdfBlob?: Blob
  error?: string
}

export const convertScreenshotToPdf = async (imageData: string): Promise<ScreenshotToPdfResult> => {
  try {
    if (!imageData || typeof imageData !== 'string') {
      throw new Error('Invalid image data provided')
    }

    const isValidImageUrl = imageData.startsWith('data:image/') || imageData.startsWith('blob:')
    if (!isValidImageUrl) {
      throw new Error('Image data must be either a data URL or blob URL')
    }

    const pdfBlob = await generatePdfBlob(React.createElement(ScreenshotPdf, { imageData }))

    return {
      success: true,
      pdfBlob
    }
  } catch (error) {
    const errorMessage = `Failed to convert screenshot to PDF: ${error instanceof Error ? error.message : String(error)}`

    console.error('Screenshot to PDF conversion failed:', {
      error: errorMessage,
      hasImageData: !!imageData,
      imageDataLength: imageData?.length || 0,
      imageDataType: imageData?.startsWith('data:') ? 'data URL' : imageData?.startsWith('blob:') ? 'blob URL' : 'unknown',
      stack: error instanceof Error ? error.stack : undefined
    })

    return {
      success: false,
      error: errorMessage
    }
  }
}

export const createScreenshotPdfSection = async (imageData: string, sectionName: string): Promise<{ name: string; blob: Blob } | null> => {
  try {
    const result = await convertScreenshotToPdf(imageData)

    if (result.success && result.pdfBlob) {
      return {
        name: `${sectionName} - Screenshot`,
        blob: result.pdfBlob
      }
    }

    console.warn(`Failed to create PDF section for ${sectionName}:`, result.error)
    return null
  } catch (error) {
    console.error(`Error creating screenshot PDF section for ${sectionName}:`, error)
    return null
  }
}

export const validateImageData = (imageData: string): boolean => {
  try {
    if (!imageData || typeof imageData !== 'string') {
      return false
    }

    if (imageData.startsWith('data:image/')) {
      const base64Part = imageData.split(',')[1]
      return !!(base64Part && base64Part.length > 100)
    }

    if (imageData.startsWith('blob:')) {
      return true
    }

    return false
  } catch {
    return false
  }
}
